import Button from 'components/Button'
import ButtonLoader from 'components/ButtonLoader'
import Icon from 'components/Icon'
import { CardIcon } from 'components/Icon/icons/card'
import ListRow from 'components/ListRow'
import { PageLoader } from 'components/Page/Page.Loader'
import TextBody from 'components/TextBody'
import TextBodySmall from 'components/TextBodySmall'
import { View } from 'components/View'
import PaymentRow from 'containers/Payment/Row'
import { createLogger } from 'logger-js'
import {
  SETTINGS_MODEL_KEY,
  SETTINGS_MODEL_PAYMENT_TYPE
} from 'models/daos/settings/constants'
import openErrorBannerAction from 'modules/banner/actions/open-error'
import { CARDS_DOMAIN } from 'modules/cards/constants'
import { getTotalPriceCartSelector } from 'modules/cart/selectors/get-total'
import { getNameCurrencySelector } from 'modules/currencies/selectors'
import { createOrderAction } from 'modules/orders/actions/create'
import { isPlacingOrderSelector } from 'modules/orders/selectors/is-placing'
import { initStripeService } from 'modules/payment/services/stripe'
import REDUX_OPTION from 'modules/redux/constants/option'
import { getInstancesDomainSelector } from 'modules/redux/selectors/domain'
import getValueReduxUtil from 'modules/redux/utils/get-value'
import { updateSettingsAction } from 'modules/settings/actions'
import { STYLES_MARGIN } from 'modules/styles/constants/index.cjs'
import PropTypes from 'prop-types'
import React from 'react'
import { defineMessages } from 'react-intl'
import { connect } from 'react-redux'
import { createSelector } from 'reselect'
import { PaymentStripeForm } from './Form'
import WithIntl from 'components/WithIntl'
import { getObjectUtil } from 'packages/src'

const logger = createLogger({
  label: 'PaymentStripe'
})

const messages = defineMessages({
  securePayment: {
    id: 'PaymentStripe.securePayment',
    defaultMessage: 'Secure payment provided by Stripe.com'
  },
  orderLabel: {
    id: 'PaymentStripe.orderLabel',
    defaultMessage: 'Gymaholic Order'
  },
  placeOrder: {
    id: 'PaymentStripe.placeOrder',
    defaultMessage: 'Place Order'
  }
})

class PaymentStripeComponent extends React.Component {
  constructor (props) {
    super(props)
    this.state = {
      isUsingDifferentCard: null
    }
    this.handleClickPlaceOrder = this.handleClickPlaceOrder.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
    this.handleClickCard = this.handleClickCard.bind(this)
    this.handleClickRow = this.handleClickRow.bind(this)
  }

  async componentDidMount () {
    if (!this.props.isSelected) {
      return
    }
    this.initStripe()
  }

  componentDidUpdate (prevProps) {
    if (!prevProps.isSelected && this.props.isSelected && BROWSER) {
      this.initStripe()
    }
  }

  async initStripe () {
    try {
      await initStripeService()
    } catch (error) {
      logger.error('init stripe failed: ', error)
      openErrorBannerAction(error)
    }
  }

  getCard () {
    return getObjectUtil(this.props.cards, '0')
  }

  hasCards () {
    return !!this.props.cards.length
  }

  placeOrder (tokenId) {
    this.props.dispatch(createOrderAction({
      tokenId
    }))
  }

  handleClickCard () {
    this.setState({
      isUsingDifferentCard: true
    })
  }

  handleClickPlaceOrder () {
    this.placeOrder()
  }

  handleClickRow () {
    this.props.dispatch(updateSettingsAction(
      SETTINGS_MODEL_KEY.PAYMENT_TYPE,
      SETTINGS_MODEL_PAYMENT_TYPE.STRIPE,
      { [REDUX_OPTION.OMIT_EDITING]: true }
    ))
  }

  handleSubmit (tokenId) {
    this.placeOrder(tokenId)
  }

  renderForm () {
    if (this.hasCards() && !this.state.isUsingDifferentCard) {
      return
    }
    return (
      <div
        style={{
          margin: `${STYLES_MARGIN.BIG} 0`
        }}
      >
        <PaymentStripeForm
          onSubmit={this.handleSubmit}
          isLoading={this.props.isPlacingOrder}
          productLabel={this.props.intl.formatMessage(messages.orderLabel)}
          buttonLabel={this.props.intl.formatMessage(messages.placeOrder)}
          amount={this.props.totalPrice}
          currency={this.props.currency.toLowerCase()}
        />
      </div>
    )
  }

  renderExistingCard () {
    if (!this.hasCards() || this.state.isUsingDifferentCard) {
      return
    }
    const card = this.getCard()
    return (
      <Button
        onClick={this.handleClickCard}
        hoverableProps={{
          height: 'auto'
        }}
        style={{
          padding: 0,
          margin: `${STYLES_MARGIN.MEDIUM} auto 0`
        }}
      >
        <ListRow
          isBorderless
          align={ListRow.ALIGN.CENTER}
          style={{
            padding: 0
          }}
        >
          <ListRow.Image>
            <Icon
              icon={CardIcon}
              size={Icon.SIZE.MEDIUM}
            />
          </ListRow.Image>
          <ListRow.Text>
            <TextBody>
              <strong>{getValueReduxUtil(card, 'brand')}
              </strong> ending
              in {getValueReduxUtil(card, 'last4')}
            </TextBody>
          </ListRow.Text>
        </ListRow>
      </Button>
    )
  }

  renderRow () {
    return (
      <PaymentRow
        isSelected={this.props.isSelected}
        onClick={this.handleClickRow}
      >
        <span>Credit / Debit Card</span>
      </PaymentRow>
    )
  }

  renderPlaceOrderButton () {
    const hasCards = this.hasCards()

    if (!this.props.isSelected) {
      return
    }

    if (!hasCards || this.state.isUsingDifferentCard) {
      return
    }

    return (
      <ButtonLoader
        onClick={this.handleClickPlaceOrder}
        isLoading={this.props.isPlacingOrder}
        hoverableProps={{
          height: 'auto'
        }}
        style={{
          margin: `${STYLES_MARGIN.BIG} 0 0`,
          width: '100%'
        }}
      >
        Place Order
      </ButtonLoader>
    )
  }

  renderPaymentIsProvidedByStripe () {
    return (
      <View
        mt
      >
        <TextBodySmall>
          {this.props.intl.formatMessage(messages.securePayment)}
        </TextBodySmall>
      </View>
    )
  }

  renderLoader () {
    return <PageLoader />
  }

  renderSelectedContent () {
    if (!this.props.isSelected) {
      return
    }

    return (
      <>
        {this.renderExistingCard()}
        {this.renderForm()}
        {this.renderPaymentIsProvidedByStripe()}
        {this.renderPlaceOrderButton()}
      </>
    )
  }

  render () {
    return (
      <div
        style={{
          marginTop: STYLES_MARGIN.BIG
        }}
      >
        {this.renderRow()}
        {this.renderSelectedContent()}
      </div>
    )
  }
}

PaymentStripeComponent.propTypes = {
  dispatch: PropTypes.func.isRequired,
  payment: PropTypes.object,
  intl: PropTypes.object.isRequired,
  cards: PropTypes.arrayOf(PropTypes.object).isRequired,
  isSelected: PropTypes.bool.isRequired,
  isPlacingOrder: PropTypes.bool.isRequired,
  totalPrice: PropTypes.number.isRequired,
  currency: PropTypes.string.isRequired
}

const select = () => createSelector(
  state => state && state.payment,
  getInstancesDomainSelector(CARDS_DOMAIN),
  isPlacingOrderSelector(),
  getTotalPriceCartSelector(),
  getNameCurrencySelector(),
  (payment, cards, isPlacingOrder, totalPrice, currency) => (
    {
      cards,
      payment,
      isPlacingOrder,
      totalPrice,
      currency
    }
  )
)

const PaymentStripe = WithIntl(connect(select)(PaymentStripeComponent))

export default PaymentStripe
